'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { AuthContext } from './AuthContext';

type SocketContextValue = {
  isSocketConnected: boolean;
  socketRef: React.MutableRefObject<Socket | null>;
};

const SocketContext = createContext<SocketContextValue | null>(null);

export const SocketProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { userLoggedIn, gotedoStreamsAuthToken, setError } = useContext(AuthContext);
  const [isSocketConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const onSocketConnect = useCallback(() => {
    setIsConnected(true);
    console.log('[Gotedo Socket] Connected:', socketRef.current?.id);
  }, []);

  const onSocketDisconnect = useCallback(() => {
    setIsConnected(false);
    console.log('[Gotedo Socket] Disconnected');
  }, []);

  const onConnectError = useCallback(
    (error: Error) => {
      console.error('[Gotedo Socket] Connect error:', error);
      let reason = '';
      try {
        reason = JSON.parse(error.message)?.message ?? '';
      } catch {
        return;
      }

      if (reason) {
        setError(reason);
      }
      // setTimeout(() => {
      //   if (userLoggedIn && gotedoStreamsAuthToken) {
      //     connectSocket();
      //   }
      // }, 2000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userLoggedIn, gotedoStreamsAuthToken],
  );

  const onAnyIncoming = useCallback((event: string, ...args: any[]) => {
    console.log('[Gotedo Socket] Event received:', event, args);
  }, []);

  const onAnyOutgoing = useCallback((...event: any[]) => {
    console.log('[Gotedo Socket] Event emitted:', event);
  }, []);

  const connectSocket = useCallback(
    () => {
      if (socketRef.current) {
        disconnectSocket();
      }

      if (!gotedoStreamsAuthToken || !userLoggedIn) {
        return;
      }

      // CRITICAL: Use `websocket` only. Else 400s in clusters
      const socket = io(`${process.env.NEXT_PUBLIC_GOTEDO_API_BASE_URL}/gotedo_streams_api`, {
        reconnectionDelay: 3000,
        reconnectionDelayMax: 10000,
        retries: 10,
        auth: {
          token: gotedoStreamsAuthToken,
          /**
           * @todo Update when i18n is implemented
           */
          language: 'en',
        },
        reconnection: true,
        reconnectionAttempts: 20,
        autoConnect: false,
        path: '/ws',
        transports: ['websocket'],
      });

      socketRef.current = socket;

      // Lifecycle event listeners
      socket.on('connect', onSocketConnect);
      socket.on('disconnect', onSocketDisconnect);
      socket.on('connect_error', onConnectError);
      socket.io.on('error', (reason: Error) =>
        console.error('[Gotedo Socket] Server error:', reason),
      );
      socket.onAny(onAnyIncoming);
      socket.onAnyOutgoing(onAnyOutgoing);

      socket.connect();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userLoggedIn, gotedoStreamsAuthToken],
  );

  const disconnectSocket = useCallback(() => {
    if (!socketRef.current) return;

    socketRef.current.off('connect', onSocketConnect);
    socketRef.current.off('disconnect', onSocketDisconnect);
    socketRef.current.off('connect_error', onConnectError);
    socketRef.current.offAny(onAnyIncoming);
    socketRef.current.offAnyOutgoing(onAnyOutgoing);
    socketRef.current.disconnect();
    socketRef.current = null;
    setIsConnected(false);
  }, [onSocketConnect, onSocketDisconnect, onConnectError, onAnyIncoming, onAnyOutgoing]);

  useEffect(() => {
    if (userLoggedIn && gotedoStreamsAuthToken) {
      connectSocket();
    }
    return () => {
      disconnectSocket();
    };
  }, [userLoggedIn, gotedoStreamsAuthToken, connectSocket, disconnectSocket]);

  const value = useMemo(() => ({ isSocketConnected, socketRef }), [isSocketConnected]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

// Custom hook to consume
export function useSocketContext(): SocketContextValue {
  const ctx = useContext(SocketContext);
  if (!ctx) {
    throw new Error('useSocketContext must be used inside <SocketProvider>');
  }
  return ctx;
}
